import React from "react";
import styled from "styled-components/macro";
import {
    CalendarCheckmarkRegular,
    ClockRegular,
    HomeRegular,
    WeatherSunnyRegular,
} from "@fluentui/react-icons";
import { Skeleton } from "@fluentui/react-northstar";

import {
    fmtNumber,
    fmtRecoveryDetail,
    fmtSignedHoursValue,
} from "./format";

/**
 * Bandeau de KPI
 * @param {{ items: { label: string, value: string, unit: string, sub?: string, icon: JSX.Element }[], loading?: boolean }} props
 */
export const KpiBanner = ({ items = [], loading = false }) => (
    <Banner>
        {items.map((item) => (
            <Kpi key={item.label}>
                <IconCircle>{item.icon}</IconCircle>
                <Body>
                    <Label>{item.label}</Label>
                    {loading ? (
                        <Skeleton animation="wave">
                            <Skeleton.Line width="80px" height="28px" />
                        </Skeleton>
                    ) : (
                        <ValueLine>
                            <Value>{item.value}</Value>
                            <Unit>{item.unit}</Unit>
                        </ValueLine>
                    )}
                    {item.sub && !loading && <Sub>{item.sub}</Sub>}
                </Body>
            </Kpi>
        ))}
    </Banner>
);

const ICON_PROPS = { fontSize: 16, color: "#5B5FC7" };

/** KPI de la synthèse annuelle à partir de la réponse kpis du back */
export const buildSummaryKpis = (kpis) => [
    {
        label: "Jours travaillés",
        value: fmtNumber(kpis?.days_worked),
        unit: "j",
        icon: <ClockRegular {...ICON_PROPS} />,
    },
    {
        label: "Vacances",
        value: fmtNumber(kpis?.days_vacation),
        unit: "j",
        icon: <WeatherSunnyRegular {...ICON_PROPS} />,
    },
    {
        label: "Télétravail",
        value: fmtNumber(kpis?.days_telework),
        unit: "j",
        icon: <HomeRegular {...ICON_PROPS} />,
    },
    {
        label: "Récupération annuelle",
        value: fmtSignedHoursValue(kpis?.recovery_hours),
        unit: "h",
        sub: kpis ? fmtRecoveryDetail(kpis) : "",
        icon: <CalendarCheckmarkRegular {...ICON_PROPS} />,
    },
];

const Banner = styled.div`
    background: #edebe9;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-wrap: wrap;
    gap: 1px;
`;

const Kpi = styled.div`
    flex: 1 1 170px;
    min-width: 0;
    background: #fff;
    display: flex;
    align-items: center;
    gap: clamp(10px, 1.2vw, 16px);
    padding: clamp(14px, 1.8vw, 24px);
    min-height: clamp(84px, 7vw, 104px);
`;

const IconCircle = styled.div`
    width: clamp(32px, 3.2vw, 44px);
    height: clamp(32px, 3.2vw, 44px);
    border-radius: 50%;
    background: #e8ebfa;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

const Label = styled.span`
    font-size: 13px;
    color: #616161;
`;

const ValueLine = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
`;

const Value = styled.span`
    font-size: clamp(22px, 2vw, 28px);
    font-weight: 700;
    line-height: 1.15;
    color: #212121;
`;

const Unit = styled.span`
    font-size: 14px;
    color: #616161;
`;

const Sub = styled.span`
    font-size: 12px;
    color: #616161;
`;
